import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  ArrowRight,
  Check,
  Trophy,
  Users,
  Clock,
  Target,
  Zap,
  Building,
  MapPin,
  Briefcase,
  Pizza,
  Send,
  Phone,
  Mail,
  ShieldCheck,
  Star,
  Waves,
  Landmark,
  TreePine,
  Sun,
  Compass,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ──────────────────────────────────────────────────────────────
   CONSTANTS
────────────────────────────────────────────────────────────── */
const D10 = {
  phoneDisplay: "+385 95 501 1065",
  phoneE164: "385955011065",
  email: "d10spizzafantasista@gmail.com",
};

/* ──────────────────────────────────────────────────────────────
   DATA
────────────────────────────────────────────────────────────── */
const STATS = [
  { value: "Zero", label: "Dull slide decks", icon: Briefcase },
  { value: "100%", label: "Hands-on teamwork", icon: Users },
  { value: "∞", label: "Pizza & good vibes", icon: Pizza },
];

const FORMATS = [
  {
    title: "The Home Fixture",
    subtitle: "At D10 Pizza Academy",
    desc: "Walk into our professional kitchen, tie on an apron, and forget the office exists. We handle the space, the drinks, and the cleanup — your only job is to stretch dough, talk strategy, and eat extremely well.",
    points: [
      "Seats up to 15 guests comfortably",
      "Full bar & beverage packages included",
      "Private, immersive kitchen environment",
      "Everything prepped — just bring your team",
    ],
    icon: Building,
    image: "/img/academy/d10pizza-academy-1.jpg",
    color: "#0087E6",
  },
  {
    title: "The Away Game",
    subtitle: "At Your Office or Venue",
    desc: "We load our portable high-temperature ovens, the freshest ingredients, and our expert instructors into a van and turn your courtyard, rooftop, or office car park into a pop-up Neapolitan pizzeria.",
    points: [
      "Flexible capacity — scales to 50+ guests",
      "We bring ovens, dough, and all equipment",
      "Works indoors or outdoors",
      "Ideal for summer parties & club events",
    ],
    icon: MapPin,
    image: "/img/academy/d10pizza-academy-13.jpg",
    color: "#FFD700",
  },
];

const PACKAGES = [
  {
    id: "friendly",
    title: "The Friendly Match",
    level: "Casual Bonding",
    time: "2.5 Hours",
    price: "From €65 / person",
    desc: "Low-pressure, high-fun. Your team learns the basics of hand-stretching and topping, shares a few drinks at the bar, and sits down to eat exactly what they built — together.",
    highlights: [
      "Welcome aperitivo & drinks",
      "Guided dough-stretching session",
      "All-you-can-eat pizza feast",
      "Relaxed sit-down dining to close",
    ],
    icon: Users,
  },
  {
    id: "tournament",
    title: "The Tournament",
    level: "Competitive Team Building",
    time: "3.5 Hours",
    price: "From €85 / person",
    desc: "Now it gets serious. Split into rival squads, design a signature pizza under pressure, and face the judgement of our expert pizza masters on taste, presentation, and teamwork.",
    highlights: [
      "Mystery-box ingredient challenge",
      "Timed kitchen battles",
      "Winning team lifts the D10 Trophy",
      "Post-competition feast for all",
    ],
    icon: Trophy,
    featured: true,
  },
  {
    id: "executive",
    title: "The Strategy Retreat",
    level: "Workshop + Dining",
    time: "Half Day",
    price: "Custom Pricing",
    desc: "Merge your quarterly planning or board offsite with a culinary experience. Present on the big screen, then move seamlessly from spreadsheets to sourdough — ending the day on a high.",
    highlights: [
      "Screen, projector & AV setup",
      "Coffee & pastry welcome",
      "Structured hands-on workshop",
      "Premium wine & craft beer pairings",
    ],
    icon: Target,
  },
];

const B2B_FAQ = [
  {
    q: "Can you handle dietary restrictions?",
    a: "Absolutely. We offer excellent vegetarian, vegan, and gluten-free options as standard. Just share your team's requirements at least 48 hours before the event.",
  },
  {
    q: "Do you issue corporate invoices (R1)?",
    a: "Yes. Every corporate event can be fully invoiced to your company with standard payment terms — no hassle.",
  },
  {
    q: "What is the minimum / maximum group size?",
    a: "At our Academy we recommend 6–15 guests so everyone gets real hands-on time. For 'Away Games' at your venue, we scale comfortably to 50+ people.",
  },
  {
    q: "What do we need to provide for an 'Away Game'?",
    a: "Just power outlets and hungry people. We bring the portable ovens, tables, ingredients, aprons, and everything else.",
  },
];

const PAG_HIGHLIGHTS = [
  {
    title: "Pag Old Town",
    desc: "Wander the 15th-century stone streets designed by Juraj Dalmatinac — the same architect behind Šibenik's famous cathedral. End with a sundowner on the waterfront.",
    icon: Landmark,
    tag: "History",
  },
  {
    title: "Paški Sir Cheese",
    desc: "Pag's iconic sheep cheese is aged by sea-salt winds and wild herbs. Visit a local dairy, taste the real thing, and bring a wheel home as the ultimate souvenir.",
    icon: Star,
    tag: "Gastronomy",
  },
  {
    title: "Ancient Olive Groves of Lun",
    desc: "Over 1,500 olive trees — some more than a thousand years old — dot the landscape near the island's tip. A peaceful walk through living history.",
    icon: TreePine,
    tag: "Nature",
  },
  {
    title: "Beaches & Hidden Coves",
    desc: "From the famous Zrće strip to tucked-away pebble coves like Ručica and Sveti Duh, Pag's coastline offers crystal-clear Adriatic water at every turn.",
    icon: Waves,
    tag: "Beaches",
  },
  {
    title: "Solana Salt Fields",
    desc: "Croatia's largest salt works have operated here for centuries. The white salt flats against the turquoise bay are as photogenic as they are historic.",
    icon: Sun,
    tag: "Heritage",
  },
  {
    title: "National Parks Nearby",
    desc: "Use Pag as your base camp. Kornati archipelago, Paklenica canyon, and Plitvice Lakes are all within easy day-trip distance.",
    icon: Compass,
    tag: "Day Trips",
  },
];

const GAME_FLOW = [
  {
    num: "01",
    title: "Warm-Up",
    desc: "Aprons on, drinks poured, introductions made. Your instructor sets the game plan for the session.",
  },
  {
    num: "02",
    title: "Training",
    desc: "Hands in dough. Everyone learns to stretch, sauce, and top under expert guidance. It gets messy — that's the point.",
  },
  {
    num: "03",
    title: "Match Day",
    desc: "Teams compete, collaborate, or both. Design your signature pizza, fire it in 90 seconds, and own the result.",
  },
  {
    num: "04",
    title: "The Feast",
    desc: "Sit down together and eat everything you made. Cold drinks, warm pizza, and the kind of conversation that doesn't happen in meetings.",
  },
];

const WHY_ITEMS = [
  {
    icon: Users,
    iconBg: "bg-[#F0F9FF]",
    iconColor: "text-[#0087E6]",
    title: "Level The Field",
    desc: "Job titles don't mean much when everyone's elbow-deep in dough. Pizza-making is the great equaliser — from the C-suite to the newest intern.",
  },
  {
    icon: Zap,
    iconBg: "bg-[#FFF9E6]",
    iconColor: "text-[#FFD700]",
    title: "Instant Gratification",
    desc: "Build a strategy, execute it, slide it into a 500 °C oven, and taste the results 90 seconds later. No quarterly review required.",
  },
  {
    icon: Target,
    iconBg: "bg-[#F0FFF9]",
    iconColor: "text-[#00D4AA]",
    title: "Real Collaboration",
    desc: "Stretching, timing the oven, and balancing toppings demands real-time communication. We turn your kitchen station into a high-stakes team exercise.",
  },
];

/* ──────────────────────────────────────────────────────────────
   SHARED INTERSECTION OBSERVER — one observer for the entire
   page instead of one per element. Uses a WeakMap to track
   callbacks. Elements animate in via a CSS class toggle.
────────────────────────────────────────────────────────────── */
const observerCallbacks = new WeakMap();
let sharedObserver = null;

function getSharedObserver() {
  if (sharedObserver) return sharedObserver;
  if (typeof IntersectionObserver === "undefined") return null;

  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const cb = observerCallbacks.get(entry.target);
          if (cb) cb();
          sharedObserver.unobserve(entry.target);
          observerCallbacks.delete(entry.target);
        }
      }
    },
    { rootMargin: "-30px", threshold: 0.08 }
  );

  return sharedObserver;
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = getSharedObserver();
    if (!obs) {
      setVisible(true); // SSR / no IO fallback
      return;
    }

    observerCallbacks.set(el, () => setVisible(true));
    obs.observe(el);

    return () => {
      obs.unobserve(el);
      observerCallbacks.delete(el);
    };
  }, []);

  return { ref, visible };
}

/* Lightweight reveal wrapper — CSS-only transition, no JS animation frames.
   Stagger is handled via transitionDelay in CSS, not JS timers. */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const { ref, visible } = useReveal();

  return (
    <div
      ref={ref}
      className={`reveal-fade ${visible ? "is-visible" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}s` } : undefined}
    >
      {children}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   SECTION HEADER
────────────────────────────────────────────────────────────── */
const SectionHeader = ({ eyebrow, title, subtitle, light = false, center = true }) => (
  <div className={`${center ? "text-center" : ""} mb-10 md:mb-16`}>
    {eyebrow && (
      <span
        className={`inline-block px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4 ${
          light ? "bg-white/10 text-white/90" : "bg-[#0087E6]/10 text-[#0087E6]"
        }`}
      >
        {eyebrow}
      </span>
    )}
    <h2
      className={`font-anton text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl uppercase leading-[0.9] ${
        light ? "text-white" : "text-[#002B5C]"
      }`}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        className={`mt-4 md:mt-6 font-serif italic text-base sm:text-lg md:text-xl max-w-2xl ${
          center ? "mx-auto" : ""
        } ${light ? "text-white/70" : "text-gray-500"}`}
      >
        {subtitle}
      </p>
    )}
  </div>
);

/* ──────────────────────────────────────────────────────────────
   HERO
────────────────────────────────────────────────────────────── */
const HeroCorporate = () => (
  <section className="relative min-h-[85vh] sm:min-h-[90vh] w-full overflow-hidden bg-[#002B5C] flex items-center">
    <div className="absolute inset-0">
      <img
        src="/img/academy/d10pizza-academy-14.jpg"
        alt="Team building at D10 Pizza Academy"
        className="w-full h-full object-cover"
        loading="eager"
        fetchpriority="high"
        width={1600}
        height={900}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#002B5C] via-[#002B5C]/90 to-[#002B5C]/40 md:to-transparent" />
      <div className="absolute inset-0 bg-[#0087E6]/15 mix-blend-multiply" />
    </div>

    <div className="relative z-10 container mx-auto px-4 sm:px-6 pt-24 sm:pt-20 pb-12">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/10 text-white border border-white/20 mb-5 sm:mb-6 text-[10px] sm:text-xs font-bold uppercase tracking-widest backdrop-blur-md">
          <Briefcase size={14} className="text-[#FFD700]" />
          Corporate &amp; Sports Teams
        </span>

        <h1 className="font-anton text-[2.75rem] leading-[0.88] sm:text-7xl md:text-8xl lg:text-[120px] xl:text-[130px] uppercase text-white hero-fade-in">
          Build <span className="text-[#FFD700]">Teams.</span>
          <br />
          Bake Pizza.
        </h1>

        <p className="mt-6 sm:mt-8 font-serif italic text-lg sm:text-xl md:text-2xl text-white/80 leading-relaxed border-l-4 border-[#0087E6] pl-5 sm:pl-6 max-w-xl hero-fade-in hero-fade-in--delay-1">
          Swap the conference room for a 500 °C oven. The team-building experience that actually builds teams.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 hero-fade-in hero-fade-in--delay-2">
          <a
            href="#book"
            className="group inline-flex items-center justify-center gap-3 px-7 py-4 sm:px-10 sm:py-5 bg-[#FFD700] text-[#002B5C] font-anton text-base sm:text-lg uppercase tracking-wider rounded-full hover:bg-white transition-colors duration-200 shadow-xl active:scale-[0.97]"
          >
            Plan Your Event
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   STATS BAR
────────────────────────────────────────────────────────────── */
const StatsBar = () => (
  <section className="bg-[#0087E6] relative z-20 -mt-1">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-3">
        {STATS.map((stat, i) => (
          <div
            key={i}
            className={`py-6 sm:py-8 md:py-10 text-center ${
              i < 2 ? "border-r border-white/10" : ""
            }`}
          >
            <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2 sm:mb-3 text-white/70" />
            <div className="font-anton text-xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-none">
              {stat.value}
            </div>
            <div className="text-[8px] sm:text-[10px] md:text-xs uppercase tracking-widest text-white/70 mt-1 px-1">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   THE PITCH
────────────────────────────────────────────────────────────── */
const ThePitch = () => (
  <section className="py-16 sm:py-20 md:py-32 bg-white">
    <div className="container mx-auto px-4 sm:px-6">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
        {/* Image */}
        <Reveal>
          <div className="relative">
            <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/img/academy/d10pizza-academy-1.jpg"
                alt="Teams stretching dough at D10 Pizza Academy"
                className="w-full h-[300px] sm:h-[400px] lg:h-[520px] object-cover"
                loading="lazy"
                width={800}
                height={520}
              />
            </div>
            <div className="absolute -bottom-5 -right-3 sm:-bottom-6 sm:-right-6 bg-[#FFD700] rounded-2xl px-5 py-4 sm:px-6 sm:py-5 shadow-xl">
              <div className="font-anton text-2xl sm:text-3xl text-[#002B5C] leading-none">90 sec</div>
              <div className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#002B5C]/70 mt-1">From oven to table</div>
            </div>
          </div>
        </Reveal>

        {/* Text */}
        <Reveal delay={0.1}>
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-4 bg-[#0087E6]/10 text-[#0087E6]">
              How It Works
            </span>

            <h2 className="font-anton text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.9] text-[#002B5C] mb-6">
              No Slides.<br />
              <span className="text-[#0087E6]">Just Pizza.</span><br />
              And Your Team.
            </h2>

            <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              <p>
                D10 Pizza Academy runs hands-on pizza-making workshops built specifically for teams. Your group — six people or fifty — learns to stretch Neapolitan dough, balance toppings like professionals, and fire pizzas in ovens that hit 500 °C. No experience needed. No aprons-and-awkward-silence energy.
              </p>
              <p>
                We set up wherever you need us. That could mean your team walks into our fully equipped kitchen on the island of Pag, or we load our portable high-temp ovens and show up at your office, your rooftop, your sports facility — wherever your team already gathers.
              </p>
              <p>
                Every session is structured around real teamwork: communication under pressure, creative problem-solving, and shared decision-making. Then you sit down and eat exactly what you built — together.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 sm:gap-6 border-t border-gray-100 pt-8">
              {[
                { val: "6–50+", label: "Team size" },
                { val: "2.5–5h", label: "Session length" },
                { val: "Zero", label: "Skills required" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-anton text-2xl sm:text-3xl text-[#002B5C]">{s.val}</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider text-gray-400 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      {/* Game Flow */}
      <Reveal>
        <div className="max-w-6xl mx-auto mt-16 sm:mt-20 md:mt-28 border-t border-gray-100 pt-12 sm:pt-16">
          <div className="text-center mb-10 md:mb-14">
            <h3 className="font-anton text-2xl sm:text-3xl md:text-4xl uppercase text-[#002B5C]">
              The Game Flow
            </h3>
            <p className="text-gray-400 text-sm sm:text-base mt-2 font-serif italic">
              Four phases. One unforgettable team event.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-6 md:gap-8">
            {GAME_FLOW.map((step, i) => (
              <div key={i} className="relative">
                <span className="font-anton text-5xl sm:text-6xl md:text-7xl text-[#0087E6]/[0.07] leading-none block">
                  {step.num}
                </span>
                <h4 className="font-anton text-lg sm:text-xl uppercase text-[#002B5C] -mt-3 sm:-mt-4 mb-2">
                  {step.title}
                </h4>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   FORMATS (Home vs Away)
────────────────────────────────────────────────────────────── */
const Locations = () => (
  <section className="py-16 sm:py-20 md:py-32 bg-[#F0F9FF]">
    <div className="container mx-auto px-4 sm:px-6">
      <SectionHeader
        eyebrow="The Setup"
        title="Where Do We Play?"
        subtitle="Come to our training ground, or we'll bring the academy to you."
      />

      <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 max-w-6xl mx-auto">
        {FORMATS.map((format, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl border border-gray-100 group h-full flex flex-col">
              <div className="relative h-52 sm:h-64 md:h-72 overflow-hidden">
                <img
                  src={format.image}
                  alt={format.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  width={800}
                  height={450}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#002B5C]/90 via-[#002B5C]/30 to-transparent" />
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 flex items-center gap-3 sm:gap-4">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center bg-white shadow-lg flex-shrink-0">
                    <format.icon size={22} color={format.color} />
                  </div>
                  <div>
                    <h3 className="font-anton text-2xl sm:text-3xl uppercase text-white leading-tight">
                      {format.title}
                    </h3>
                    <p className="text-white/80 font-medium text-sm sm:text-base">
                      {format.subtitle}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 sm:p-8 flex-1 flex flex-col">
                <p className="text-gray-600 leading-relaxed mb-6 text-sm sm:text-base flex-1">
                  {format.desc}
                </p>
                <div className="space-y-2.5 sm:space-y-3">
                  {format.points.map((point, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#0087E6]/10 flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-[#0087E6]" />
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-[#002B5C] uppercase tracking-wide">
                        {point}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   WHY PIZZA?
────────────────────────────────────────────────────────────── */
const WhyPizza = () => (
  <section className="py-16 sm:py-20 bg-white border-b border-gray-100">
    <div className="container mx-auto px-4 sm:px-6">
      <Reveal>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-10 sm:gap-8">
          {WHY_ITEMS.map((item, i) => (
            <div key={i} className="text-center px-2 sm:px-4">
              <div
                className={`w-14 h-14 sm:w-16 sm:h-16 ${item.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-5 sm:mb-6 ${item.iconColor}`}
              >
                <item.icon size={28} />
              </div>
              <h3 className="font-anton text-xl sm:text-2xl text-[#002B5C] uppercase mb-3">
                {item.title}
              </h3>
              <p className="text-gray-500 leading-relaxed text-sm sm:text-base">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   PACKAGES
────────────────────────────────────────────────────────────── */
const Packages = () => (
  <section className="py-16 sm:py-20 md:py-32 bg-white">
    <div className="container mx-auto px-4 sm:px-6">
      <SectionHeader
        eyebrow="The Playbook"
        title="Event Formats"
        subtitle="From low-key team dinners to high-stakes kitchen battles. Pick your intensity."
      />

      <Reveal>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
          {PACKAGES.map((pkg, i) => (
            <div
              key={i}
              className={`h-full flex flex-col bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 transition-shadow duration-200 hover:shadow-2xl relative overflow-hidden group ${
                pkg.featured
                  ? "border-2 border-[#0087E6] shadow-lg ring-1 ring-[#0087E6]/20"
                  : "border-2 border-gray-100 hover:border-[#0087E6]"
              }`}
            >
              <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-[#F0F9FF] rounded-bl-full -z-10 group-hover:bg-[#0087E6]/10 transition-colors" />

              {pkg.featured && (
                <span className="absolute top-4 right-4 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider bg-[#0087E6] text-white rounded-full">
                  Popular
                </span>
              )}

              <div className="text-[10px] font-bold uppercase tracking-widest text-[#0087E6] mb-3 sm:mb-4">
                {pkg.level}
              </div>

              <h3 className="font-anton text-2xl sm:text-3xl uppercase text-[#002B5C] mb-2">
                {pkg.title}
              </h3>

              <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-5 sm:mb-6 pb-5 sm:pb-6 border-b border-gray-100 text-xs sm:text-sm text-gray-500 font-medium">
                <span className="flex items-center gap-1">
                  <Clock size={15} className="text-[#0087E6]" /> {pkg.time}
                </span>
                <span className="flex items-center gap-1">
                  <ShieldCheck size={15} className="text-[#0087E6]" /> {pkg.price}
                </span>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-6 sm:mb-8 flex-grow">
                {pkg.desc}
              </p>

              <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
                {pkg.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <Star size={15} className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#book"
                className={`w-full py-3.5 sm:py-4 text-center font-bold uppercase tracking-wider rounded-xl border-2 transition-colors duration-200 block mt-auto ${
                  pkg.featured
                    ? "bg-[#002B5C] border-[#002B5C] text-white hover:bg-[#0087E6] hover:border-[#0087E6]"
                    : "border-[#002B5C] text-[#002B5C] hover:bg-[#002B5C] hover:text-white"
                }`}
              >
                Select Format
              </a>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   DISCOVER PAG
────────────────────────────────────────────────────────────── */
const DiscoverPag = () => (
  <section className="py-16 sm:py-20 md:py-32 bg-[#F0F9FF] relative overflow-hidden">
    <div className="container mx-auto px-4 sm:px-6">
      <SectionHeader
        eyebrow="While You're Here"
        title={<>Make It a<br className="sm:hidden" /> Pag Getaway</>}
        subtitle="Fly in for the workshop, stay for the island. Here's why your team won't want to leave."
      />

      <Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
          {PAG_HIGHLIGHTS.map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 sm:p-7 border border-gray-100 hover:border-[#0087E6]/30 hover:shadow-xl transition-all duration-200 h-full flex flex-col group"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#0087E6]/10 flex items-center justify-center text-[#0087E6] group-hover:bg-[#0087E6] group-hover:text-white transition-colors duration-200 flex-shrink-0">
                  <item.icon size={20} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#0087E6]/70">
                  {item.tag}
                </span>
              </div>
              <h3 className="font-anton text-xl sm:text-2xl uppercase text-[#002B5C] mb-2 leading-tight">
                {item.title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed flex-1">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </Reveal>

      <div className="mt-10 sm:mt-14 text-center">
        <p className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto mb-6">
          Need help organising accommodation and transfers for the team? We know every corner of the island — just ask when you book.
        </p>
        <a
          href="#book"
          className="inline-flex items-center gap-2 text-[#0087E6] font-bold text-sm uppercase tracking-wider hover:text-[#002B5C] transition-colors group"
        >
          Plan your trip
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   FAQ ITEM
────────────────────────────────────────────────────────────── */
const FaqItem = React.memo(({ item, index, isOpen, toggle }) => (
  <div
    className={`border rounded-xl overflow-hidden transition-colors duration-200 ${
      isOpen
        ? "border-[#FFD700] bg-white/10 backdrop-blur"
        : "border-white/10 bg-transparent"
    }`}
  >
    <button
      onClick={() => toggle(index)}
      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left"
      aria-expanded={isOpen}
    >
      <span className="font-bold text-white pr-4 text-sm sm:text-base">{item.q}</span>
      <span
        className={`text-xl font-light transition-transform duration-200 flex-shrink-0 ${
          isOpen ? "text-[#FFD700] rotate-45" : "text-white/50"
        }`}
      >
        +
      </span>
    </button>
    <div
      className="grid transition-[grid-template-rows] duration-300 ease-out"
      style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
    >
      <div className="overflow-hidden">
        <div className="px-5 sm:px-6 pb-5 text-white/70 leading-relaxed text-sm">
          {item.a}
        </div>
      </div>
    </div>
  </div>
));

/* ──────────────────────────────────────────────────────────────
   BOOKING SECTION (FAQ + FORM)
────────────────────────────────────────────────────────────── */
const BookingSection = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    teamSize: "",
    format: "",
    date: "",
    notes: "",
  });

  const [status, setStatus] = useState({ sending: false, success: false, error: "" });
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = useCallback((i) => {
    setOpenFaq((prev) => (prev === i ? null : i));
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setStatus({ sending: true, success: false, error: "" });

      try {
        const res = await fetch(
          "https://hook.eu2.make.com/n2ptum42pe8yxnxg5qhgf4c11jkqhnma",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...formData,
              source: "corporate-events-page",
              submittedAt: new Date().toISOString(),
            }),
          }
        );

        if (!res.ok) throw new Error("Webhook responded with " + res.status);

        setStatus({ sending: false, success: true, error: "" });
        setFormData({
          companyName: "",
          contactName: "",
          email: "",
          phone: "",
          teamSize: "",
          format: "",
          date: "",
          notes: "",
        });
      } catch (err) {
        console.error("Form submission error:", err);
        setStatus({
          sending: false,
          success: false,
          error: "Something went wrong. Please try again or contact us directly.",
        });
      }
    },
    [formData]
  );

  return (
    <section id="book" className="py-16 sm:py-20 md:py-32 bg-[#002B5C] relative overflow-hidden">
      {/* Decorative blurs — simplified, no oversized elements */}
      <div
        className="absolute top-0 right-0 w-72 sm:w-[400px] h-72 sm:h-[400px] bg-[#0087E6] rounded-full blur-[120px] opacity-15 pointer-events-none"
        aria-hidden="true"
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <SectionHeader
          eyebrow="Let's Talk"
          title="Plan Your Event"
          subtitle="Tell us about your team and we'll tailor the perfect session."
          light
        />

        <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 max-w-6xl mx-auto">
          {/* FAQ Column */}
          <div className="lg:col-span-5 space-y-3 sm:space-y-4">
            <h3 className="font-anton text-2xl sm:text-3xl uppercase text-white mb-4 sm:mb-6">
              Common Questions
            </h3>

            {B2B_FAQ.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                index={i}
                isOpen={openFaq === i}
                toggle={toggleFaq}
              />
            ))}

            {/* Contact Card */}
            <div className="mt-6 sm:mt-8 p-5 sm:p-6 bg-white/5 rounded-2xl border border-white/10">
              <p className="text-white/50 text-sm mb-3 sm:mb-4">
                Prefer to talk directly?
              </p>
              <div className="space-y-3">
                <a
                  href={`mailto:${D10.email}`}
                  className="flex items-center gap-3 text-white hover:text-[#FFD700] transition-colors text-sm sm:text-base break-all"
                >
                  <Mail size={18} className="text-[#0087E6] flex-shrink-0" />
                  {D10.email}
                </a>
                <a
                  href={`tel:+${D10.phoneE164}`}
                  className="flex items-center gap-3 text-white hover:text-[#FFD700] transition-colors text-sm sm:text-base"
                >
                  <Phone size={18} className="text-[#0087E6] flex-shrink-0" />
                  {D10.phoneDisplay}
                </a>
              </div>
            </div>
          </div>

          {/* Form Column */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl">
              {status.success ? (
                <div className="text-center py-10 sm:py-12">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#00D4AA]/20 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
                    <Check size={36} className="text-[#00D4AA]" />
                  </div>
                  <h3 className="font-anton text-2xl sm:text-3xl uppercase text-[#002B5C] mb-2">
                    Request Sent!
                  </h3>
                  <p className="text-gray-500 text-sm sm:text-base">
                    We've got your brief. Our event manager will reach out within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                  <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                        Company / Organization *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.companyName}
                        onChange={(e) => updateField("companyName", e.target.value)}
                        className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-3 rounded-xl font-medium text-[#002B5C] outline-none text-sm sm:text-base"
                        placeholder="Acme Corp"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.contactName}
                        onChange={(e) => updateField("contactName", e.target.value)}
                        className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-3 rounded-xl font-medium text-[#002B5C] outline-none text-sm sm:text-base"
                        placeholder="Jane Doe"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-3 rounded-xl font-medium text-[#002B5C] outline-none text-sm sm:text-base"
                        placeholder="jane@acme.com"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                        Estimated Team Size
                      </label>
                      <select
                        value={formData.teamSize}
                        onChange={(e) => updateField("teamSize", e.target.value)}
                        className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-3 rounded-xl font-medium text-[#002B5C] outline-none cursor-pointer text-sm sm:text-base"
                      >
                        <option value="">Select size…</option>
                        <option value="Under 10">Under 10 people</option>
                        <option value="10-20">10 – 20 people</option>
                        <option value="20-50">20 – 50 people</option>
                        <option value="50+">50+ people</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                      Preferred Format
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {[
                        "The Home Fixture (At D10)",
                        "The Away Game (Your HQ)",
                      ].map((fmt) => (
                        <button
                          key={fmt}
                          type="button"
                          onClick={() => updateField("format", fmt)}
                          className={`p-3 text-xs sm:text-sm font-bold rounded-xl border-2 transition-colors leading-tight ${
                            formData.format === fmt
                              ? "border-[#0087E6] bg-[#0087E6]/10 text-[#0087E6]"
                              : "border-gray-100 bg-white text-gray-400 hover:border-gray-200"
                          }`}
                        >
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                      Event Goals &amp; Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                      rows={3}
                      className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-3 rounded-xl font-medium text-[#002B5C] outline-none resize-none text-sm sm:text-base"
                      placeholder="Holiday party? Strategy retreat? Dietary requirements we should know about early?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status.sending}
                    className="w-full py-4 sm:py-5 font-anton text-lg sm:text-xl uppercase tracking-wider rounded-xl bg-[#FFD700] text-[#002B5C] hover:bg-[#002B5C] hover:text-white transition-colors duration-200 shadow-lg flex items-center justify-center gap-3 group active:scale-[0.98] disabled:opacity-60"
                  >
                    <Send size={18} />
                    {status.sending ? "Sending Brief…" : "Request Proposal"}
                    <ArrowRight
                      className="group-hover:translate-x-1 transition-transform"
                      size={18}
                    />
                  </button>

                  {status.error && (
                    <p className="text-red-500 text-sm text-center mt-2">
                      {status.error}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   FONTS — preconnect + async stylesheet in head
────────────────────────────────────────────────────────────── */
const useFonts = () => {
  useEffect(() => {
    if (document.querySelector('link[data-d10-fonts]')) return;

    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = "https://fonts.googleapis.com";
    document.head.appendChild(preconnect);

    const preconnect2 = document.createElement("link");
    preconnect2.rel = "preconnect";
    preconnect2.href = "https://fonts.gstatic.com";
    preconnect2.crossOrigin = "anonymous";
    document.head.appendChild(preconnect2);

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@0,400;0,600;1,400&display=swap";
    link.setAttribute("data-d10-fonts", "true");
    link.media = "print"; // load non-blocking
    link.onload = () => { link.media = "all"; }; // apply once loaded
    document.head.appendChild(link);
  }, []);
};

/* ──────────────────────────────────────────────────────────────
   MAIN EXPORT
────────────────────────────────────────────────────────────── */
export default function CorporateEventsPage() {
  useFonts();

  return (
    <div className="bg-white min-h-screen selection:bg-[#0087E6] selection:text-white overflow-x-hidden">
      <style>{`
        /* ── Base typography ── */
        .corporate-page { font-family: 'Manrope', system-ui, sans-serif; }
        .corporate-page .font-anton { font-family: 'Anton', system-ui, sans-serif; letter-spacing: 0.02em; }
        .corporate-page .font-serif { font-family: 'Playfair Display', Georgia, serif; }
        html { scroll-behavior: smooth; }

        /* ── Reveal animation — single CSS class, no JS animation frames ── */
        .reveal-fade {
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 0.4s ease, transform 0.4s ease;
        }
        .reveal-fade.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── Hero entrance — CSS keyframes, no JS ── */
        .hero-fade-in {
          animation: heroIn 0.5s ease both;
        }
        .hero-fade-in--delay-1 { animation-delay: 0.15s; }
        .hero-fade-in--delay-2 { animation-delay: 0.3s; }

        @keyframes heroIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Reduce motion for users who prefer it ── */
        @media (prefers-reduced-motion: reduce) {
          .reveal-fade,
          .hero-fade-in {
            opacity: 1 !important;
            transform: none !important;
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <Navbar />

      <main className="corporate-page">
        <HeroCorporate />
        <StatsBar />
        <ThePitch />
        <Locations />
        <WhyPizza />
        <Packages />
        <DiscoverPag />
        <BookingSection />
      </main>

      <Footer />
    </div>
  );
}